import { Injectable, InjectionToken } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { environment } from '../../../environments/environment';

export interface FuncionIntegranteDetalle {
  id?: number;
  n_funcion: number;
  n_detalle?: number;
  descripcion_detalle?: string;
  estado?: number;
  fechaCreacion?: string | Date;
  fechaModificacion?: string | Date;
  funcionIntegrante?: {
    id?: number;
    descripcion?: string;
  };
  funcionesDetIntegrantes?: {
    id?: number;
    descripcion?: string;
  };
}

export const FUNCION_INTEGRANTE_DETALLE_SERVICE = new InjectionToken<FuncionIntegranteDetalleService>(
  'FUNCION_INTEGRANTE_DETALLE_SERVICE',
);

@Injectable({
  providedIn: 'root',
})
export class FuncionIntegranteDetalleService {
  private apiUrl = `${environment.apiUrl}/api/funcion-integrante-detalle`;

  constructor(private http: HttpClient) {}

  private manejarError(error: HttpErrorResponse): Observable<never> {
    console.error('❌ Error en funcion-integrante-detalle:', error);
    let mensaje = 'Ocurrió un error inesperado.';

    if (error.status === 400) {
      mensaje = error.error?.message || 'Datos inválidos.';
    } else if (error.status === 401) {
      mensaje = 'No estás autenticado. Inicia sesión nuevamente.';
    } else if (error.status === 403) {
      mensaje = 'No tienes permisos para realizar esta acción.';
    } else if (error.status === 404) {
      mensaje = 'El registro no fue encontrado.';
    } else if (error.status === 409) {
      mensaje = 'El registro ya existe.';
    } else if (error.status === 500) {
      mensaje = 'Error en el servidor. Inténtalo más tarde.';
    }

    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: mensaje,
    });

    return throwError(() => new Error(mensaje));
  }

  getAll(): Observable<FuncionIntegranteDetalle[]> {
    return this.http.get<any>(this.apiUrl).pipe(
      map((res) => (Array.isArray(res) ? res : (res?.data ?? []))),
      catchError((error) => this.manejarError(error)),
    );
  }

  getActivos(): Observable<FuncionIntegranteDetalle[]> {
    return this.http.get<any>(`${this.apiUrl}/activos`).pipe(
      map((res) => (Array.isArray(res) ? res : (res?.data ?? []))),
      catchError((error) => this.manejarError(error)),
    );
  }

  getById(id: number): Observable<FuncionIntegranteDetalle> {
    return this.http.get<FuncionIntegranteDetalle>(`${this.apiUrl}/${id}`).pipe(
      catchError((error) => this.manejarError(error)),
    );
  }

  create(data: FuncionIntegranteDetalle): Observable<any> {
    return this.http.post<any>(this.apiUrl, data).pipe(
      catchError((error) => this.manejarError(error)),
    );
  }

  update(id: number, data: Partial<FuncionIntegranteDetalle>): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${id}`, data).pipe(
      catchError((error) => this.manejarError(error)),
    );
  }

  remove(id: number): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/${id}`).pipe(
      catchError((error) => this.manejarError(error)),
    );
  }

  activate(id: number): Observable<any> {
    return this.http.patch<any>(`${this.apiUrl}/${id}/activate`, {}).pipe(
      catchError((error) => this.manejarError(error)),
    );
  }
}